import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Clock, Search, Filter, MoreVertical, Plus, 
  CheckCircle, X, AlertTriangle, Calendar
} from 'lucide-react';
import { formatDistanceToNow, format, isPast } from 'date-fns';
import { useReminders } from '@/hooks/useReminders';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const RemindersPage = () => {
  const reminders = useReminders();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-danger/10 text-danger border-danger/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      medium: 'bg-brand/10 text-brand border-brand/20',
      low: 'bg-text-muted/10 text-text-muted border-text-muted/20',
    };
    
    return (
      <Badge className={`${colors[priority as keyof typeof colors] || colors.medium} text-xs`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  const filteredReminders = reminders.data.filter(reminder => {
    const matchesSearch = reminder.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         reminder.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const isOverdue = reminder.due_at && isPast(new Date(reminder.due_at));
    
    let matchesFilter = true;
    switch (filterStatus) {
      case 'overdue':
        matchesFilter = isOverdue;
        break;
      case 'today':
        matchesFilter = reminder.due_at && 
                       format(new Date(reminder.due_at), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
        break;
      case 'upcoming':
        matchesFilter = reminder.due_at && !isPast(new Date(reminder.due_at));
        break;
      case 'no-due-date':
        matchesFilter = !reminder.due_at;
        break;
      default:
        matchesFilter = true;
    }
    
    return matchesSearch && matchesFilter;
  });

  const handleStatusChange = (id: string, status: 'done' | 'dismissed') => {
    reminders.updateStatus({ id, status });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Clock className="h-8 w-8 text-warning" />
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Reminders</h1>
            <p className="text-text-secondary">Keep track of important tasks and deadlines</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {reminders.overdueCount > 0 && (
            <Badge className="bg-danger text-white">
              {reminders.overdueCount} overdue
            </Badge>
          )}
          <Button className="bg-brand hover:bg-brand-600">
            <Plus className="h-4 w-4 mr-2" />
            New Reminder
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search reminders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-bg-subtle border-border-subtle focus:border-brand"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-bg-subtle border border-border-subtle rounded-md text-text-primary focus:border-brand focus:outline-none"
              >
                <option value="all">All Reminders</option>
                <option value="overdue">Overdue</option>
                <option value="today">Due Today</option>
                <option value="upcoming">Upcoming</option>
                <option value="no-due-date">No Due Date</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reminders List */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Reminders ({filteredReminders.length})</span>
            {filteredReminders.length > 0 && (
              <span className="text-sm font-normal text-text-secondary">
                Showing {filteredReminders.length} of {reminders.data.length} reminders
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="max-h-[600px]">
            <div className="divide-y divide-border-subtle">
              {filteredReminders.map((reminder) => {
                const isOverdue = reminder.due_at && isPast(new Date(reminder.due_at));
                
                return (
                  <div
                    key={reminder.id}
                    className={`p-4 hover:bg-hover-bg transition-colors ${
                      isOverdue ? 'bg-danger/5 border-l-2 border-l-danger' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between space-x-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${
                          isOverdue ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
                        }`}>
                          {isOverdue ? (
                            <AlertTriangle className="h-4 w-4" />
                          ) : (
                            <Clock className="h-4 w-4" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className={`text-sm font-medium ${
                              isOverdue ? 'text-danger' : 'text-text-primary'
                            }`}>
                              {reminder.title}
                            </h3>
                            {getPriorityBadge(reminder.priority)}
                            {isOverdue && (
                              <Badge className="bg-danger text-white text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>
                          
                          {reminder.description && (
                            <p className="text-sm text-text-muted mb-2 line-clamp-2">
                              {reminder.description}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-xs text-text-muted">
                            {reminder.due_at ? (
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3" />
                                <span className={isOverdue ? 'text-danger' : ''}>
                                  Due {formatDistanceToNow(new Date(reminder.due_at), { addSuffix: true })}
                                </span>
                              </div>
                            ) : (
                              <span>No due date</span>
                            )}
                            <span>
                              Created {formatDistanceToNow(new Date(reminder.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(reminder.id, 'done')}
                          disabled={reminders.isUpdating}
                          className="text-success hover:bg-success/10"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="p-1 h-auto">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(reminder.id, 'done')}
                            >
                              <CheckCircle className="h-4 w-4 mr-2 text-success" />
                              Mark as Done
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(reminder.id, 'dismissed')}
                            >
                              <X className="h-4 w-4 mr-2 text-text-muted" />
                              Dismiss
                            </DropdownMenuItem>
                            <Separator />
                            <DropdownMenuItem>
                              <Calendar className="h-4 w-4 mr-2" />
                              Edit Due Date
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Clock className="h-4 w-4 mr-2" />
                              Snooze
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredReminders.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 mx-auto text-text-muted opacity-50 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    {reminders.data.length === 0 ? 'No reminders yet' : 'No matching reminders'}
                  </h3>
                  <p className="text-text-muted mb-4">
                    {reminders.data.length === 0 
                      ? 'Create your first reminder to keep track of important tasks.'
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                  {reminders.data.length === 0 && (
                    <Button className="bg-brand hover:bg-brand-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Reminder
                    </Button>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default RemindersPage;